import React, { useState, useCallback, useRef, useEffect } from "react";
import Button from "components/atoms/Button";
import FormInput from "components/atoms/FormInput";
import RadioAndCheckInput from "components/atoms/RadioAndCheckInputGroup/RadioAndCheckInput";
import Toggle from "components/atoms/Toggle";
import { connect } from "react-redux";
import { toggleAppEnv } from "redux/app/app.action";
import { getValues } from "services/storage";
import profileEdit from "../../../assets/svg/user-edit-black.svg";
import AppModal from "../../../components/organisms/CustomModal";
import showPwd from "../../../assets/svg/show.svg";
import hidePwd from "../../../assets/svg/hide.svg";
import Alert from "components/atoms/Alert";
import { BASE_URL, username } from "services/http";
import axios from "axios";
import { handleException } from "../../../utils";
import Spinner from "components/atoms/Spinner";
import { logoutHandler } from "redux/auth/auth.action";

const checkboxData = [{ label: "Are you a developer" }];

const UserProfile = ({
  appEnv,
  toggleAppEnv,
  user_data,
  primaryPocketData,
  bearerToken,
  publicKey,
  pocketDetails,
  logoutHandler,
}) => {
  const [firstname, setFirstname] = useState("");
  const [lastname, setLastname] = useState("");
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [showModal, setShowModal] = useState(false);
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [password, setPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [passwordErr, setPasswordErr] = useState("");

  const [showCurrentPassword, setShowCurrentPassword] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [errorState, setErrorState] = useState(false);

  const [otp, setOtp] = useState(["", "", "", "", "", ""]);
  const [timer, setTimer] = useState(59);
  const otpInputRefs = useRef<(HTMLInputElement | null)[]>([]);

  const handleOtpChange = (index, value) => {
    if (value.length > 1) return;

    const newOtp = [...otp];
    newOtp[index] = value;
    setOtp(newOtp);

    if (value && index < 5) {
      otpInputRefs.current[index + 1]?.focus();
    }
  };

  const handleOtpKeyDown = (index, e) => {
    if (e.key === "Backspace" && !otp[index] && index > 0) {
      otpInputRefs.current[index - 1]?.focus();
    }
  };

  const handleResendOtp = async () => {
    if (timer > 0) {
      Alert("info", `Please wait ${timer} seconds before resending OTP`);
      return;
    }

    setLoading(true);
    try {
      const res = await axios.post(
        `${BASE_URL}user/initiate-password-change`,
        {
          username: username || default_email,
          oldPassword: password,
          newPassword,
          confirmNewPassword: confirmPassword,
        },
        {
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${bearerToken}`,
            "Public-Key": `${publicKey}`,
          },
        },
      );

      setLoading(false);

      if (res?.data?.code !== "error") {
        Alert("success", "New OTP has been sent to your email");
        setTimer(59);
        setOtp(["", "", "", "", "", ""]);
        otpInputRefs.current[0]?.focus();
      } else {
        Alert("error", res.data.message || "Failed to resend OTP");
      }
    } catch (error) {
      setLoading(false);
      handleException(error);
    }
  };

  const handleProceedOtp = () => {
    const otpValue = otp.join("");
    if (otpValue.length === 6) {
      ChangePasswordFunc();
    } else {
      Alert("error", "Please enter complete OTP");
    }
  };

  const handleCloseOtpModal = () => {
    setShowConfirmModal(false);
    setShowModal(true);
    setOtp(["", "", "", "", "", ""]);
    setTimer(59);
  };

  useEffect(() => {
    let interval;
    if (showConfirmModal && timer > 0) {
      interval = setInterval(() => {
        setTimer((prev) => prev - 1);
      }, 1000);
    }
    return () => clearInterval(interval);
  }, [showConfirmModal, timer]);

  const username = primaryPocketData?.pocketOwner?.emailAddress;
  const default_firstname = primaryPocketData?.pocketOwner?.firstName;
  const default_lastname = primaryPocketData?.pocketOwner?.lastName;
  const default_email = primaryPocketData?.pocketOwner?.emailAddress;
  const default_phoneNumber = primaryPocketData?.pocketOwner?.phoneNumber;

  const handleReset = () => {
    setFirstname("");
    setLastname("");
    setEmail("");
    setPhone("");
  };

  const mainURL = `user/change-password`;

  const ChangePasswordFunc = useCallback(async () => {
    const otpValue = otp.join("");

    if (otpValue.length !== 6) {
      Alert("error", "Please enter the 6-digit OTP");
      return;
    }

    if (!newPassword || !confirmPassword) {
      Alert("error", "Incomplete Form data input");
      return;
    }

    if (newPassword !== confirmPassword) {
      Alert("error", "Password do not Match");
      return;
    }

    setLoading(true);
    try {
      const res = await axios.post(
        `${BASE_URL + mainURL}`,
        {
          otp: otp.join(""),
          newPassword: newPassword,
          username: username,
          oldPassword: password,
        },
        {
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${bearerToken}`,
            "Public-Key": `${publicKey}`,
          },
        },
      );
      if (res.data.code === "error") {
        setLoading(false);
        Alert("error", res.data.message);
      } else {
        setLoading(false);
        Alert("success", res.data.message);
        setShowModal(false);
        setShowConfirmModal(false);
        setOtp(["", "", "", "", "", ""]);
        logoutHandler();
        localStorage.clear();
        sessionStorage.clear();
        // Redirect to login page after successful password change
        setTimeout(() => {
          window.location.href = "/auth/login";
        }, 2000);
      }
    } catch (error) {
      setLoading(false);
      setErrorState(true);
      handleException(error);
    }
  }, [otp, newPassword, confirmPassword, username]);

  const requestPasswordChangeOtp = useCallback(async () => {
    if (!newPassword || !confirmPassword) {
      Alert("error", "Please enter new password");
      return;
    }

    if (newPassword !== confirmPassword) {
      Alert("error", "Passwords do not match");
      return;
    }

    setLoading(true);
    try {
      const res = await axios.post(
        `${BASE_URL}user/initiate-password-change`,
        {
          username: username || default_email,
          oldPassword: password,
          newPassword,
          confirmNewPassword: confirmPassword,
        },
        {
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${bearerToken}`,
            "Public-Key": `${publicKey}`,
          },
        },
      );

      setLoading(false);

      if (res?.data?.code !== "error") {
        Alert("success", "OTP has been sent to your email");
        setShowModal(false);
        setShowConfirmModal(true);
      } else {
        Alert("error", res.data.message || "Failed to send OTP");
      }
    } catch (error) {
      setLoading(false);
      handleException(error);
    }
  }, [username, default_email, newPassword, confirmPassword]);

  return (
    <>
      <div className="flex flex-col gap-1 mb-12">
        <span className="text-lg font-normal text-black leading-5">
          Your Personal Information
        </span>
        <span className="text-[15px] font-normal leading-5 text-DarkBg6">
          Update your personal and contact information.
        </span>
      </div>
      <div className="flex flex-col gap-5 w-full desktop:w-[715px]">
        <div className="flex flex-col gap-5 w-full">
          <span className="bg-GrayCustom11 w-[96px] h-[96px] rounded-full flex justify-center items-center">
            <img src={profileEdit} alt="profile_icon" className="" />
          </span>
          <form className="flex flex-col gap-5">
            <div className="flex mobile:flex-col gap-5 justify-between items-center">
              <FormInput
                type="text"
                name="firstname"
                placeholder="Firstname"
                inputStyle="border border-transparent text-Black2"
                label="First name"
                labelStyle="font-normal text-base leading-6 text-DarkBg6"
                style="w-full"
                onChange={(e) => setFirstname(e.target.value)}
                value={firstname === "" ? default_firstname : firstname}
                validationErr=""
                disabled={true}
              />
              <FormInput
                type="text"
                name="lastname"
                placeholder="Lastname"
                inputStyle="border border-transparent text-Black2"
                label="Last name"
                labelStyle="font-normal text-base leading-6 text-DarkBg6"
                style="w-full"
                onChange={(e) => setLastname(e.target.value)}
                value={lastname === "" ? default_lastname : lastname}
                validationErr=""
                disabled={true}
              />
            </div>
            <div className="flex mobile:flex-col gap-5 justify-between items-center">
              <FormInput
                type="email"
                name="email"
                placeholder="example@gmail.com"
                inputStyle="border border-transparent text-Black2"
                label="Email address"
                labelStyle="font-normal text-base leading-6 text-DarkBg6"
                style="w-full"
                onChange={(e) => setEmail(e.target.value)}
                value={email === "" ? default_email : email}
                disabled={true}
                validationErr=""
              />
              <FormInput
                type="text"
                name="phone"
                placeholder="+234"
                inputStyle="border border-transparent text-Black2"
                label="Phone number"
                labelStyle="font-normal text-base leading-6 text-DarkBg6"
                style="w-full"
                onChange={(e) => setPhone(e.target.value)}
                value={phone === "" ? default_phoneNumber : phone}
                validationErr=""
                disabled={true}
              />
            </div>
            <div className="flex flex-col gap-8">
              {/* <span className="w-full flex flex-col justify-between">
                <RadioAndCheckInput
                  type="checkbox"
                  name="currency"
                  inputData={checkboxData}
                  checkStyle=""
                />
              </span> */}
            </div>
          </form>
        </div>
        <div className="">
          <div className="border-b pb-2">
            <span className="text-lg">Profile security</span>
          </div>
          <div className="flex flex-col gap-12">
            <div className="flex items-center justify-start gap-24 mobile:gap-5 py-5">
              <span>
                <p className="font-normal text-[15px] leading-5 text-black">
                  Change your password
                </p>
                <p className="font-normal text-xs leading-6 text-Black4">
                  You can change your password here easily
                </p>
              </span>
              <Button
                onClick={() => setShowModal(true)}
                btnType="button"
                btnText="Change Password"
                btnStyle="bg-Accent_blue text-SecondaryAccent5 font-semibold text-sm mobile:text-xs leading-7 px-4 py-2 !rounded-lg mobile:w-full truncate"
              />
            </div>
            {/* <div className="flex justify-center items-center gap-5">
              <Button
                btnType="button"
                btnText="Cancel"
                btnStyle="bg-SecondaryAccent3 text-black font-semibold text-sm leading-7 px-5 py-1 !rounded-lg mobile:w-full"
                onClick={handleReset}
              />
              <Button
                btnType="button"
                btnText="Save changes"
                btnStyle="bg-black text-SecondaryAccent5 font-semibold text-sm leading-7 px-5 py-1 !rounded-lg mobile:w-full"
              />
            </div> */}
          </div>
        </div>
      </div>
      {showModal && (
        <AppModal
          modalStyle="desktop:w-[28%] w-3/5 mobile:w-4/5 min-h-fit z-30 top-0 left-0 right-0 p-4 my-[10rem] mobile:my-[5rem] mx-auto animate-center_align rounded-md"
          contentStyle="overflow-y-scroll min-h-fit w-full"
          handleClose={() => setShowModal(false)}
        >
          <div className=" bg-white p-10 w-full rounded-lg">
            <h5 className="text-[24px] font-semibold mb-6">Set new password</h5>
            <div>
              <p className="text-[15px] mb-2">
                Enter your new password in the fields below and click save. Your
                new password must be at least 8 characters long and contain a
                mix of letters and numbers.
              </p>
            </div>

            <FormInput
              type={showCurrentPassword ? "text" : "password"}
              name="passwd"
              placeholder="********"
              inputStyle="border border-transparent text-Black2 flex-row-reverse"
              label="Old Password"
              labelStyle="font-normal text-left text-Black2 mx-0 mt-6"
              onChange={(e) => setPassword(e.target.value)}
              value={password}
              validationErr={passwordErr}
              img={showCurrentPassword ? showPwd : hidePwd}
              imgStyle="cursor-pointer w-5 h-5 block"
              imgOnClick={() =>
                setShowCurrentPassword((prevState) => !prevState)
              }
            />
            <FormInput
              type={showPassword ? "text" : "password"}
              name="passwd"
              placeholder="********"
              inputStyle="border border-transparent text-Black2 flex-row-reverse"
              label="New Password"
              labelStyle="font-normal text-left text-Black2 mx-0 mt-6"
              onChange={(e) => setNewPassword(e.target.value)}
              value={newPassword}
              validationErr={passwordErr}
              img={showPassword ? showPwd : hidePwd}
              imgStyle="cursor-pointer w-5 h-5 block"
              imgOnClick={() => setShowPassword((prevState) => !prevState)}
            />
            <FormInput
              type={showNewPassword ? "text" : "password"}
              name="passwd"
              placeholder="********"
              inputStyle="border border-transparent text-Black2 flex-row-reverse"
              label="Enter Password Again"
              labelStyle="font-normal text-left text-Black2 mx-0 mt-6"
              onChange={(e) => setConfirmPassword(e.target.value)}
              value={confirmPassword}
              validationErr={passwordErr}
              img={showNewPassword ? showPwd : hidePwd}
              imgStyle="cursor-pointer w-5 h-5 block"
              imgOnClick={() => setShowNewPassword((prevState) => !prevState)}
            />
            <Button
              onClick={requestPasswordChangeOtp}
              btnType="button"
              btnText={loading ? <Spinner /> : "Confirm Password"}
              btnStyle="mt-10 w-full bg-black text-SecondaryAccent5 font-semibold text-sm leading-7 px-5 py-2 !rounded-[8px] mobile:w-full"
              disabled={loading}
            />
          </div>
        </AppModal>
      )}
      {/* {showConfirmModal && (
        <AppModal
          modalStyle="desktop:w-[40%] w-3/5 mobile:w-4/5 min-h-fit z-30 top-0 left-0 right-0 p-4 my-[10rem] mobile:my-[5rem] mx-auto animate-center_align rounded-md"
          contentStyle="overflow-y-scroll min-h-fit w-full"
          handleClose={() => setShowConfirmModal(false)}
        >
          <div className="text-center bg-white p-10 w-full rounded-lg">
            <h5>Change Password</h5>

            <FormInput
              type={showCurrentPassword ? "text" : "password"}
              name="passwd"
              placeholder="********"
              inputStyle="border border-transparent text-Black2 flex-row-reverse"
              label="Current Password"
              labelStyle="font-normal text-left text-Black2 mx-0 mt-6"
              onChange={(e) => setPassword(e.target.value)}
              value={password}
              validationErr={passwordErr}
              img={showCurrentPassword ? showPwd : hidePwd}
              imgStyle="cursor-pointer w-5 h-5 block"
              imgOnClick={() =>
                setShowCurrentPassword((prevState) => !prevState)
              }
            />
            <FormInput
              type={showPassword ? "text" : "password"}
              name="passwd"
              placeholder="********"
              inputStyle="border border-transparent text-Black2 flex-row-reverse"
              label="New Password"
              labelStyle="font-normal text-left text-Black2 mx-0 mt-6"
              onChange={(e) => setNewPassword(e.target.value)}
              value={newPassword}
              validationErr={passwordErr}
              img={showPassword ? showPwd : hidePwd}
              imgStyle="cursor-pointer w-5 h-5 block"
              imgOnClick={() => setShowPassword((prevState) => !prevState)}
            />
            <FormInput
              type={showNewPassword ? "text" : "password"}
              name="passwd"
              placeholder="********"
              inputStyle="border border-transparent text-Black2 flex-row-reverse"
              label="Confirm Password"
              labelStyle="font-normal text-left text-Black2 mx-0 mt-6"
              onChange={(e) => setConfirmPassword(e.target.value)}
              value={confirmPassword}
              validationErr={passwordErr}
              img={showNewPassword ? showPwd : hidePwd}
              imgStyle="cursor-pointer w-5 h-5 block"
              imgOnClick={() => setShowNewPassword((prevState) => !prevState)}
            />
            <Button
              onClick={ChangePasswordFunc}
              btnType="button"
              btnText={
                loading ? (
                  <Spinner /> // <Spinner text="Loading..." textStyle="text-white" />
                ) : (
                  "Change Password"
                )
              }
              btnStyle="mt-10 bg-black text-SecondaryAccent5 font-semibold text-sm leading-7 px-5 py-1 !rounded-lg mobile:w-full"
              disabled={loading}
            />
          </div>
        </AppModal>
      )} */}

      {showConfirmModal && (
        <AppModal
          modalStyle="desktop:w-[28%] w-3/5 mobile:w-4/5 min-h-fit z-30 top-0 left-0 right-0 p-4 my-[10rem] mobile:my-[5rem] mx-auto animate-center_align rounded-3xl"
          contentStyle="overflow-y-scroll min-h-fit w-full"
          handleClose={handleCloseOtpModal}
          closeBtnStyle="z-50 w-6 h-6 mr-2"
        >
          <div className="text-center bg-white p-10 w-full rounded-3xl relative">
            {/* Title */}
            <h2 className="text-[24px] font-bold text-center mb-4 text-gray-900">
              Enter One Time Passcode
            </h2>

            {/* Description */}
            <p className="text-center text-gray-600 text-[15px] mb-10">
              Please enter the 6-digit OTP sent to
              <br />
              {default_email?.replace(/(.{2})(.*)(@.*)/, "$1***$3") ||
                "your email"}
            </p>

            {/* OTP Input Fields */}
            <div className="flex justify-center gap-3 mb-8">
              {[0, 1, 2, 3, 4, 5].map((index) => (
                <input
                  key={index}
                  ref={(el) => (otpInputRefs.current[index] = el)}
                  type="text"
                  inputMode="numeric"
                  maxLength={1}
                  value={otp[index] || ""}
                  onChange={(e) => handleOtpChange(index, e.target.value)}
                  onKeyDown={(e) => handleOtpKeyDown(index, e)}
                  className="w-14 h-16 text-center text-3xl font-light text-gray-400 border-2 border-gray-200 rounded-2xl focus:border-gray-400 focus:outline-none transition-colors"
                />
              ))}
            </div>

            {/* Resend OTP */}
            <p className="text-center text-gray-600 text-[15px] mb-10">
              Didn't receive the OTP?{" "}
              <button
                className={`font-medium ${
                  timer > 0
                    ? "text-gray-400 cursor-not-allowed"
                    : "text-red-500 hover:text-red-600 cursor-pointer"
                }`}
                onClick={handleResendOtp}
                disabled={timer > 0}
              >
                Resend{" "}
                {timer > 0 && `(00:${timer.toString().padStart(2, "0")})`}
              </button>
            </p>

            {/* Proceed Button */}
            <Button
              onClick={handleProceedOtp}
              btnType="button"
              btnText={loading ? <Spinner /> : "Proceed"}
              btnStyle="w-full bg-black text-white py-3 rounded-[8px] text-lg font-medium hover:bg-gray-800 transition-colors"
              disabled={loading}
            />
          </div>
        </AppModal>
      )}
    </>
  );
};

const mapStateToProps = (state) => ({
  appEnv: state.app.appEnv,
  user_data: state.auth.user_data,
  primaryPocketData: state.pocket.primaryPocket_details,
  bearerToken: state.auth.token,
  publicKey: state.auth.public_key,
  pocketDetails: state.auth.primaryPocket_details,
});

const mapDispatchToProps = (dispatch) => ({
  toggleAppEnv: () => dispatch(toggleAppEnv()),
  logoutHandler: () => dispatch(logoutHandler()),
});

export default connect(mapStateToProps, mapDispatchToProps)(UserProfile);
